import { useSelector } from "react-redux";

import { type RootState } from "@/store/store";

import { hasBackendMenu, isMenuVisible, type MenuVisibility } from "./nav";

export interface MenuVisibilityState extends MenuVisibility {
  /**
   * Whether visibility is resolved yet. Until it is, the rail shows nothing
   * rather than the permissive legacy menu.
   */
  known: boolean;
  /** Whether the backend answered at all, as opposed to an older session. */
  backendResolved: boolean;
  isAllowed: (menuId: string) => boolean;
}

/**
 * What this caller may open, from the backend's login context.
 *
 * One source for both the rail and the route guard: hiding a menu entry never
 * stopped anyone typing the URL, and a guard that read visibility even slightly
 * differently would bounce callers off pages the rail had just offered them.
 *
 * @param contextResolved whether `/auth/me` has answered (or failed). Once it
 * has we stop withholding, so a session minted before this contract existed
 * falls back to the scope rules instead of an empty rail forever.
 */
export function useMenuVisibility(contextResolved: boolean): MenuVisibilityState {
  const visibleMenuIds = useSelector((state: RootState) => state.auth.user?.visibleMenuIds);
  const manageableLevels = useSelector((state: RootState) => state.auth.user?.manageableLevels);

  const visibility: MenuVisibility = { visibleMenuIds, manageableLevels };
  const backendResolved = hasBackendMenu(visibility);

  return {
    ...visibility,
    known: backendResolved || contextResolved,
    backendResolved,
    isAllowed: (menuId: string) => isMenuVisible(menuId, visibility),
  };
}
